import { useEffect, useState } from 'react'
import { collection, query, where } from 'firebase/firestore'
import { REGIONS } from '@shared/regions'
import { db } from '../lib/firebase'
import { getCountFromServer } from '../lib/firestore'
import { yesterdayLocalISO } from '../lib/formatDate'
import { log } from '../lib/logger'

const TTL_MS = 10 * 60_000

let cached: { at: number; counts: Map<string, number> } | null = null
let inFlight: Promise<Map<string, number>> | null = null

function fetchCounts(): Promise<Map<string, number>> {
  inFlight ??= Promise.all(
    REGIONS.map(r =>
      getCountFromServer(
        query(
          collection(db, 'events'),
          where('regionId', '==', r.id),
          where('startDate', '>=', yesterdayLocalISO()),
        ),
      ).then(snap => [r.id, snap.data().count] as const),
    ),
  )
    .then(entries => {
      const counts = new Map(entries.filter(([, n]) => n > 0))
      cached = { at: Date.now(), counts }
      return counts
    })
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

/**
 * Upcoming published events per region, for the region picker. One count query
 * per region (~1 read each), fetched only once `enabled` (the picker is open)
 * and reused for a few minutes. Undefined until known — counts need the
 * network, so offline the picker simply shows none.
 */
export function useRegionEventCounts(enabled: boolean): Map<string, number> | undefined {
  const [counts, setCounts] = useState(() => cached?.counts)

  useEffect(() => {
    if (!enabled) return
    if (cached && Date.now() - cached.at < TTL_MS) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- adopt the module cache another picker filled
      setCounts(cached.counts)
      return
    }
    let live = true
    fetchCounts()
      .then(next => { if (live) setCounts(next) })
      .catch(err => log('regions', 'region counts failed', { error: String(err) }))
    return () => { live = false }
  }, [enabled])

  return counts
}
